/**
 * German label resolution — LV §123-126, Arch §9.
 *
 * The enum values are English and binding (LV §27, §29). The German display labels come
 * from `GET /configuration/enums` at runtime, so **no German string for a backend value
 * appears anywhere in this codebase**. Renaming "TECHNISCH FERTIG" is then a
 * configuration change, not a frontend release — and the label a user sees cannot drift
 * from the value the state machine uses.
 *
 * The status→colour mapping *is* here, because it is a presentation decision rather
 * than a business rule: a released order shows blue and a cancelled one grey, and no
 * backend field carries that.
 */

import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { Badge, type BadgeTone } from "@/components";
import { viewerTimeZone } from "@/lib/plant-time";
import { configurationService } from "@/services";
import type { EnumCatalogue, PriorityDefinition } from "@/types/api";

interface LabelContextValue {
  enums: EnumCatalogue;
  priorities: PriorityDefinition[];
  /** Resolve one value. Falls back to the raw value rather than showing nothing. */
  label: (enumName: string, value: string | null | undefined) => string;
  /** The options for a `<select>`, in the backend's order. */
  options: (enumName: string) => { value: string; label: string }[];
  priority: (code: string) => PriorityDefinition | undefined;
  ready: boolean;
}

const LabelContext = createContext<LabelContextValue | null>(null);

/**
 * Loads the label catalogue once and shares it.
 *
 * `staleTime: Infinity` because these are configuration tables: they change when an
 * administrator changes them, not while a user works. Re-fetching them per page would
 * add a round trip to every navigation for data that has not moved.
 */
export function LabelProvider({ children }: { children: ReactNode }) {
  const enumsQuery = useQuery({
    queryKey: ["configuration", "enums"],
    queryFn: () => configurationService.enums(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const prioritiesQuery = useQuery({
    queryKey: ["configuration", "priorities"],
    queryFn: () => configurationService.priorities(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const value = useMemo<LabelContextValue>(() => {
    const enums = enumsQuery.data ?? {};
    const priorities = prioritiesQuery.data ?? [];

    return {
      enums,
      priorities,
      label: (enumName, raw) => {
        if (!raw) return "—";
        // Falling back to the raw value keeps a screen usable when the catalogue has
        // not loaded, or when the backend has a value the label map does not cover
        // yet. Showing an empty cell would hide information the user needs.
        return enums[enumName]?.labels_de[raw] ?? raw;
      },
      options: (enumName) => {
        const entry = enums[enumName];
        if (!entry) return [];
        return entry.values.map((value) => ({
          value,
          label: entry.labels_de[value] ?? value,
        }));
      },
      priority: (code) => priorities.find((item) => item.code === code),
      ready: enumsQuery.isSuccess && prioritiesQuery.isSuccess,
    };
  }, [
    enumsQuery.data,
    enumsQuery.isSuccess,
    prioritiesQuery.data,
    prioritiesQuery.isSuccess,
  ]);

  return <LabelContext.Provider value={value}>{children}</LabelContext.Provider>;
}

export function useLabels(): LabelContextValue {
  const context = useContext(LabelContext);
  if (!context)
    throw new Error("useLabels muss innerhalb von LabelProvider verwendet werden.");
  return context;
}

// ==================================================================== status colours
/**
 * Work order status → badge tone (LV §29's twelve statuses).
 *
 * A presentation decision, so it lives in the frontend. The *labels* do not.
 */
const WORK_ORDER_TONES: Record<string, BadgeTone> = {
  DRAFT: "neutral",
  NEW: "info",
  PLANNED: "info",
  READY_FOR_RELEASE: "warning",
  RELEASED: "accent",
  IN_PROGRESS: "accent",
  TECHNICALLY_COMPLETED: "ok",
  COMPLETED: "ok",
  CLOSED: "neutral",
  ON_HOLD: "warning",
  CANCELLED: "neutral",
  REJECTED: "danger",
};

const INSPECTION_TONES: Record<string, BadgeTone> = {
  DRAFT: "neutral",
  IN_PROGRESS: "info",
  COMPLETED: "ok",
  APPROVED: "ok",
  CANCELLED: "neutral",
};

/** LV §45's five severities. CRITICAL and RED both read as danger — they are. */
const SEVERITY_TONES: Record<string, BadgeTone> = {
  INFO: "info",
  GREEN: "ok",
  YELLOW: "warning",
  RED: "danger",
  CRITICAL: "danger",
};

const CONDITION_TONES: Record<string, BadgeTone> = {
  GOOD: "ok",
  ACCEPTABLE: "ok",
  DEGRADED: "warning",
  CRITICAL: "danger",
};

const CLASSIFICATION_TONES: Record<string, BadgeTone> = {
  OK: "ok",
  WARNING: "warning",
  CRITICAL: "danger",
  OUT_OF_RANGE: "info",
  NOT_EVALUATED: "neutral",
};

const DOCUMENT_TONES: Record<string, BadgeTone> = {
  DRAFT: "neutral",
  IN_REVIEW: "warning",
  APPROVED: "ok",
  SUPERSEDED: "info",
  OBSOLETE: "neutral",
  ARCHIVED: "neutral",
};

const FAILURE_TONES: Record<string, BadgeTone> = {
  REPORTED: "danger",
  ASSESSED: "warning",
  IN_REPAIR: "accent",
  FUNCTION_TESTED: "info",
  CAUSE_ASSESSED: "ok",
  CLOSED: "neutral",
};

const ASSET_TONES: Record<string, BadgeTone> = {
  PLANNED: "neutral",
  INSTALLED: "info",
  ACTIVE: "ok",
  STANDBY: "info",
  OUT_OF_SERVICE: "warning",
  DEFECTIVE: "danger",
  REMOVED: "neutral",
  SCRAPPED: "neutral",
};

const SITE_TONES: Record<string, BadgeTone> = {
  PLANNED: "neutral",
  CONSTRUCTION: "info",
  COMMISSIONING: "info",
  OPERATION: "ok",
  PARTIAL_SHUTDOWN: "warning",
  SHUTDOWN: "danger",
  DECOMMISSIONED: "neutral",
};

/**
 * LV §64. Procurement lifecycle, not stock — a part can be `OBSOLETE` and still sit on
 * the shelf in quantity. Only the two that mean "plan a successor" carry a tone.
 */
const OBSOLESCENCE_TONES: Record<string, BadgeTone> = {
  ACTIVE: "ok",
  DISCONTINUATION_ANNOUNCED: "warning",
  OBSOLETE: "danger",
  REPLACED: "neutral",
};

/**
 * #414. Whether a part is free for the next order, across the plants the reader sees.
 * Fully reserved is the one to look at before promising it; none is the one to reorder.
 * Geblockt (decision 162) shares the red of "Kein Bestand": in both, nothing on the
 * shelf can be taken, and the label says which of the two it is.
 */
const AVAILABILITY_TONES: Record<string, BadgeTone> = {
  FREE: "ok",
  PARTLY_RESERVED: "info",
  FULLY_RESERVED: "warning",
  NO_STOCK: "danger",
  BLOCKED: "danger",
};

/**
 * #466, decision 170. A request still waiting on somebody is the one to look at; once
 * ordered it is in hand, delivered is done, and a cancelled one is history. Existing
 * tones only, so no new colour pair (LV §193).
 */
const REORDER_TONES: Record<string, BadgeTone> = {
  OPEN: "warning",
  ORDERED: "info",
  DELIVERED: "ok",
  CANCELLED: "neutral",
};

const TONE_MAPS: Record<string, Record<string, BadgeTone>> = {
  MaterialAvailability: AVAILABILITY_TONES,
  ReorderRequestStatus: REORDER_TONES,
  ObsolescenceStatus: OBSOLESCENCE_TONES,
  WorkOrderStatus: WORK_ORDER_TONES,
  InspectionStatus: INSPECTION_TONES,
  FindingSeverity: SEVERITY_TONES,
  ConditionClass: CONDITION_TONES,
  MeasurementClassification: CLASSIFICATION_TONES,
  DocumentStatus: DOCUMENT_TONES,
  FailureStatus: FAILURE_TONES,
  AssetStatus: ASSET_TONES,
  SiteStatus: SITE_TONES,
};

/** A status as a badge, label and colour resolved together. */
export function StatusBadge({
  enumName,
  value,
  prefix,
}: {
  enumName: string;
  value: string | null | undefined;
  /**
   * Names what the status is of, where the badge stands beside another one — the
   * material list's „Nachbestellung: Offen“ next to its availability (#466). The label
   * after the colon is still the server's.
   */
  prefix?: string;
}) {
  const { label } = useLabels();
  if (!value) return <span className="muted">—</span>;
  const tone = TONE_MAPS[enumName]?.[value] ?? "neutral";
  return (
    <Badge tone={tone} title={value}>
      {prefix ? `${prefix}: ` : null}
      {label(enumName, value)}
    </Badge>
  );
}

/**
 * A priority as a badge, with its reaction time in the tooltip.
 *
 * The reaction time comes from `priority_definition` (LV §28), so the tooltip shows
 * DAH's configured value rather than a number written into the UI.
 */
export function PriorityBadge({ code }: { code: string | null | undefined }) {
  const { priority } = useLabels();
  if (!code) return <span className="muted">—</span>;

  const definition = priority(code);
  // Tone from `sort_order`, not from the literal "P1": priorities are configuration and
  // may be renamed (LV §28), but the most urgent one is always sorted first.
  const order = definition?.sort_order ?? 99;
  const tone: BadgeTone =
    order <= 1 ? "danger" : order === 2 ? "warning" : order === 3 ? "info" : "neutral";

  const reaction = definition?.reaction_time_minutes;
  const title = reaction
    ? `${definition?.name ?? code} · Reaktionszeit ${formatMinutes(reaction)}`
    : (definition?.name ?? code);

  return (
    <Badge tone={tone} title={title}>
      {code}
    </Badge>
  );
}

/** Criticality class as a badge (LV §55). Its own palette — an A asset is not an error. */
export function CriticalityBadge({
  criticalityClass,
  score,
}: {
  criticalityClass: string | null | undefined;
  score?: string | null;
}) {
  const { label } = useLabels();
  if (!criticalityClass) return <span className="muted">nicht bewertet</span>;

  const tone = criticalityClass.toLowerCase() as "a" | "b" | "c";
  return (
    <Badge tone={tone} title={score ? `Score ${score}` : undefined}>
      {label("CriticalityClass", criticalityClass)}
    </Badge>
  );
}

// ==================================================================== formatting
/**
 * Format a timestamp for display.
 *
 * The API sends ISO 8601 UTC (LV §116); the reader sees local time, because a technician
 * reading a completion time wants the time they were standing there, not UTC.
 *
 * `timeZone` is the site's, from `Site.timezone` (LV §197) — pass it wherever the screen
 * is about one plant. The server decides what a site's zone is; this function only
 * renders it, which is the difference between formatting and business logic (LV §134).
 * Without one it falls back to `viewerTimeZone()`, which `src/lib/plant-time.ts` shares
 * with the inverse conversion a form needs, so a time typed and a time shown use one zone.
 */
export function formatDateTime(
  value: string | null | undefined,
  timeZone?: string | null,
): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: timeZone ?? viewerTimeZone(),
  });
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  // A date-only value has no timezone and must not be shifted by one — `2026-08-14`
  // parsed as UTC midnight and rendered in Berlin would still be the 14th, but the
  // reverse case at the end of a month would not be.
  const [year, month, day] = value.slice(0, 10).split("-");
  if (!year || !month || !day) return value;
  return `${day}.${month}.${year}`;
}

/**
 * Format money for display.
 *
 * Takes the decimal **string** the API sends (LV §117) and formats it without ever
 * making it a JS number — `parseFloat` on a cost figure is how rounding error enters an
 * audited record.
 */
export function formatMoney(
  value: string | null | undefined,
  currency = "EUR",
): string {
  if (value === null || value === undefined || value === "") return "—";
  const [whole, fraction = "00"] = value.split(".");
  const grouped = (whole ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const symbol = currency === "EUR" ? "€" : currency;
  return `${grouped},${fraction.padEnd(2, "0").slice(0, 2)} ${symbol}`;
}

/**
 * Format a quantity string, trimming trailing zeros that carry no information.
 *
 * The decimal separator is a comma, like every other number on the screen. This is
 * not cosmetic: German writes `1.250` for one thousand two hundred fifty, so a
 * quantity rendered as `1.25` can be read as three orders of magnitude larger than
 * it is. Found on the work-order screen, where `1.25 h` sat beside `28.08.2026` and
 * `1.234,50 €` — see `formatMoney`, which already got this right.
 *
 * Grouping is deliberately *not* applied. A stock figure or an hour count is rarely
 * four digits, and a thousands separator that is itself a dot is the exact ambiguity
 * being removed here.
 */
export function formatQuantity(
  value: string | null | undefined,
  unit?: string,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const trimmed = value.includes(".") ? value.replace(/\.?0+$/, "") : value;
  const german = trimmed.replace(".", ",");
  return unit ? `${german} ${unit}` : german;
}

/**
 * Whether a quantity string is zero in any spelling the API or a form hands back — `0`,
 * `-0`, `0.0000`, and the shapes the decimal schemas take since #316: `0,`, `0.`, `,0`,
 * `.00`. Missing those let "greater than zero" pass a zero. On the string, because a
 * quantity never passes through `Number` (LV §117).
 */
export function istNullmenge(value: string): boolean {
  return /^-?(0+([.,]0*)?|[.,]0+)$/.test(value.trim());
}

/** Minutes as a human duration — the unit `priority_definition` uses (LV §28). */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
  }
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  return hours ? `${days} T ${hours} h` : `${days} T`;
}

/** A KPI value with its unit, or a dash when the KPI has no basis to compute. */
export function formatKpi(value: string | null, unit: string | null): string {
  if (value === null) return "—";
  const trimmed = value.includes(".") ? value.replace(/\.?0+$/, "") : value;
  const withComma = trimmed.replace(".", ",");
  if (!unit) return withComma;
  if (unit === "%") return `${withComma} %`;
  if (unit === "EUR") return formatMoney(value);
  return `${withComma} ${unit}`;
}

/** Days until a date, negative when overdue. Drives the due-date colouring. */
export function daysUntil(date: string | null | undefined): number | null {
  if (!date) return null;
  const target = new Date(`${date.slice(0, 10)}T00:00:00Z`).getTime();
  const today = new Date();
  const midnight = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  return Math.round((target - midnight) / 86_400_000);
}

/** A due date with its urgency shown. Overdue is red; the count is what matters. */
export function DueDate({ date }: { date: string | null | undefined }) {
  const days = daysUntil(date);
  if (days === null) return <span className="muted">—</span>;

  const tone: BadgeTone = days < 0 ? "danger" : days <= 7 ? "warning" : "neutral";
  const suffix =
    days < 0 ? `${Math.abs(days)} T überfällig` : days === 0 ? "heute" : `in ${days} T`;

  return (
    <span className="row" style={{ gap: "0.5rem" }}>
      <span className="num">{formatDate(date)}</span>
      <Badge tone={tone}>{suffix}</Badge>
    </span>
  );
}
